import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import { CartContext } from '../context/CartContext';
import Toast from './Toast';
import ProductCard from './ProductCard';
import EnlargeableImage from './ui/EnlargableImage';
import 'react-quill-new/dist/quill.snow.css';
import '../styles/PageProductDetails.css';

function PageProductDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { isLoggedIn } = useContext(AuthContext);
    const { fetchCart } = useContext(CartContext);

    const [product, setProduct] = useState(null);
    const [quantity, setQuantity] = useState(1);
    const [selectedColor, setSelectedColor] = useState('');
    const [similarProducts, setSimilarProducts] = useState([]);
    const [relatedProducts, setRelatedProducts] = useState([]);
    const [isFavorite, setIsFavorite] = useState(false);
    const [categoryNames, setCategoryNames] = useState([]);
    const [toast, setToast] = useState('');
    const [loading, setLoading] = useState(true);

    const cleanDescription = product?.description
        ? product.description.replace(/&nbsp;/g, ' ').replace(/\u00a0/g, ' ')
        : '';

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [id]);

    useEffect(() => {
        const fetchProductData = async () => {
            setLoading(true);
            try {
                // Pobieranie szczegółów produktu z uwzględnieniem zarchiwizowanych (dla widoku po bezpośrednim ID)
                const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/products/${id}`);
                const prodData = res.data;
                setProduct(prodData);

                if (prodData.colors && prodData.colors.length > 0) {
                    setSelectedColor(prodData.colors[0]);
                }

                // Pobieranie podobnych produktów
                try {
                    const simRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/products/${id}/similar`);
                    setSimilarProducts(simRes.data || []);
                } catch (simErr) {
                    console.error('Błąd pobierania podobnych produktów:', simErr);
                }

                // Pobieranie powiązanych produktów z populowanego pola
                if (prodData.relatedProducts && prodData.relatedProducts.length > 0) {
                    const relatedList = await Promise.all(
                        prodData.relatedProducts.map((item) => {
                            const relId = typeof item === 'string' ? item : item._id;
                            return axios
                                .get(`${process.env.REACT_APP_API_URL}/api/products/${relId}`)
                                .then((r) => r.data)
                                .catch(() => null);
                        })
                    );
                    setRelatedProducts(relatedList.filter(Boolean));
                } else {
                    setRelatedProducts([]);
                }
            } catch (error) {
                console.error('Błąd pobierania produktu:', error);
                setToast('Nie udało się pobrać danych produktu.');
            } finally {
                setLoading(false);
            }
        };

        if (id) fetchProductData();
    }, [id]);

    // Mapowanie ID kategorii na nazwy
    useEffect(() => {
        if (!product) return;
        const resolveNames = async () => {
            try {
                const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/categories`);
                const categories = res.data || [];
                
                const resolved = (product.categories || []).map((cat) => {
                    const found = categories.find((c) => c._id === cat.category);
                    return {
                        categoryName: found?.name || 'Kategoria',
                        subCategory: cat.subCategory || ''
                    };
                });
                setCategoryNames(resolved);
            } catch (err) {
                console.error('Błąd pobierania kategorii:', err);
            }
        };
        resolveNames();
    }, [product]);

    // Sprawdzenie statusu ulubionych
    useEffect(() => {
        const checkFavorite = async () => {
            if (!isLoggedIn || !product) return;
            try {
                const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/favorites`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
                });
                const favorites = res.data.items || [];
                setIsFavorite(favorites.some((fav) => fav.product?._id === product._id));
            } catch (error) {
                console.error('Błąd pobierania ulubionych:', error);
            }
        };
        checkFavorite();
    }, [isLoggedIn, product]);

    if (loading) {
        return (
            <div className="pp-wrapper">
                <div className="pp-overlay">Ładowanie produktu...</div>
            </div>
        );
    }

    if (!product) {
        return (
            <div className="pp-wrapper">
                <div className="pp-overlay">Nie znaleziono produktu.</div>
            </div>
        );
    }

    const availableStock = Math.max(0, (product.stock || 0) - (product.reserved || 0));
    const isOutOfStock = availableStock <= 0;

    const handleAddToCart = async () => {
        if (isOutOfStock) return;
        if (!isLoggedIn) {
            setToast('Musisz być zalogowany, aby dodać produkt do koszyka.');
            navigate('/login');
            return;
        }

        if (quantity > availableStock) {
            setToast(`Nie można dodać do koszyka. Dostępna ilość: ${availableStock}`);
            return;
        }

        try {
            await axios.post(
                `${process.env.REACT_APP_API_URL}/api/cart/add`,
                { productId: product._id, quantity, color: selectedColor || undefined },
                { headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` } }
            );
            await fetchCart();
            setToast('✅ Produkt dodany do koszyka!');
        } catch (error) {
            console.error('Błąd dodawania do koszyka:', error);
            setToast('❌ Błąd podczas dodawania do koszyka.');
        }
    };

    const handleToggleFavorite = async () => {
        if (!isLoggedIn) {
            setToast('Musisz być zalogowany, aby dodać produkt do ulubionych.');
            navigate('/login');
            return;
        }

        try {
            if (isFavorite) {
                await axios.delete(
                    `${process.env.REACT_APP_API_URL}/api/favorites/remove/${product._id}`,
                    { headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` } }
                );
                setIsFavorite(false);
                setToast('💔 Usunięto z ulubionych');
            } else {
                await axios.post(
                    `${process.env.REACT_APP_API_URL}/api/favorites/add`,
                    { productId: product._id },
                    { headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` } }
                );
                setIsFavorite(true);
                setToast('❤️ Dodano do ulubionych!');
            }
        } catch (error) {
            console.error('Błąd przy zmianie statusu ulubionego:', error);
        }
    };

    return (
        <div className={`pp-wrapper ${isOutOfStock ? 'out-of-stock-page' : ''}`}>
            <div className="pp-overlay">
                <div className="pp-content-container">
                    <div className="pp-main-section">
                        <div className="pp-left-column">
                            
                            {/* Tu wstawiamy nasz nowy komponent ze zdjęciami */}
                            <EnlargeableImage 
                                images={product.images} 
                                productName={product.name} 
                                isOutOfStock={isOutOfStock} 
                            />

                            {cleanDescription && (
                                <div className="pp-description">
                                    <h2>Opis produktu</h2>
                                    <div className="ql-container ql-snow" style={{ border: 'none' }}>
                                        <div 
                                            className="pp-description-content ql-editor"
                                            style={{ padding: '10px 0', wordBreak: 'break-word' }}
                                            dangerouslySetInnerHTML={{ __html: cleanDescription }} 
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="pp-info">
                            <h1 className="pp-title">{product.name}</h1>
                            <p className="pp-internal-code">Kod produktu: <strong>{product.internalCode}</strong></p>
                            
                            {categoryNames.length > 0 && (
                                <div className="pp-meta">
                                    <div className="pp-categories">
                                        <strong>Kategorie:</strong>
                                        <div className="pp-categories-list">
                                            {categoryNames.map((cat, idx) => (
                                                <span key={idx} className="pp-category-item">
                                                    {cat.categoryName}{cat.subCategory && ` / ${cat.subCategory}`}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="pp-pricing-section">
                                <div className="pp-price-main">
                                    <span className="pp-price-label">Cena:</span>
                                    <span className="pp-price-value">{Number(product.price).toFixed(2)} PLN</span>
                                </div>
                                {product.lowestPriceLast30Days != null && (
                                    <div className="pp-price-promo">
                                        <span className="pp-promo-text">
                                            Najniższa cena z ostatnich 30 dni: {Number(product.lowestPriceLast30Days).toFixed(2)} PLN
                                        </span>
                                    </div>
                                )}
                                <div className="pp-stock-info">
                                    <span className={`pp-stock-status ${availableStock > 5 ? 'pp-stock-good' : availableStock > 0 ? 'pp-stock-low' : 'pp-stock-none'}`}>
                                        {availableStock > 0 ? `Dostępność: ${availableStock} szt.` : 'Brak w magazynie'}
                                    </span>
                                </div>
                            </div>

                            {product.colors && product.colors.length > 0 && (
                                <div className="pp-colors">
                                    <strong>Wybierz kolor:</strong>
                                    <div className="pp-color-list">
                                        {product.colors.map((color, idx) => (
                                            <button
                                                key={idx}
                                                className={`pp-color-badge ${selectedColor === color ? 'selected' : ''}`}
                                                onClick={() => setSelectedColor(color)}
                                            >
                                                {color}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="pp-controls">
                                {!isOutOfStock && (
                                    <div className="pp-quantity">
                                        <label htmlFor="quantity">Ilość:</label>
                                        <input
                                            id="quantity"
                                            type="number"
                                            min="1"
                                            max={availableStock}
                                            value={quantity}
                                            onChange={(e) =>
                                                setQuantity(Math.max(1, Math.min(parseInt(e.target.value, 10) || 1, availableStock)))
                                            }
                                        />
                                    </div>
                                )}

                                <div className="pp-actions">
                                    <button
                                        className="pp-buy-btn"
                                        onClick={handleAddToCart}
                                        disabled={isOutOfStock}
                                    >
                                        {isOutOfStock ? 'Brak w magazynie' : 'Dodaj do koszyka'}
                                    </button>

                                    <button
                                        className={`pp-fav-btn ${isFavorite ? 'active' : ''}`}
                                        onClick={handleToggleFavorite}
                                    >
                                        {isFavorite ? '❤️ Usuń z ulubionych' : '🤍 Dodaj do ulubionych'}
                                    </button>
                                </div>
                            </div>

                            {(product.height != null || product.width != null || product.depth != null || product.thickness != null || product.length != null || product.weight != null) && (
                                <div className="pp-dimensions">
                                    <h3>Wymiary i waga produktu</h3>
                                    <div className="pp-dimensions-grid">
                                        {product.height != null && <div className="pp-dim-item"><span>Wysokość:</span> <strong>{Number(product.height).toFixed(1)} cm</strong></div>}
                                        {product.width != null && <div className="pp-dim-item"><span>Szerokość:</span> <strong>{Number(product.width).toFixed(1)} cm</strong></div>}
                                        {product.depth != null && <div className="pp-dim-item"><span>Głębokość:</span> <strong>{Number(product.depth).toFixed(1)} cm</strong></div>}
                                        {product.thickness != null && <div className="pp-dim-item"><span>Grubość:</span> <strong>{Number(product.thickness).toFixed(1)} mm</strong></div>}
                                        {product.length != null && <div className="pp-dim-item"><span>Długość:</span> <strong>{Number(product.length).toFixed(1)} cm</strong></div>}
                                        {product.weight != null && <div className="pp-dim-item"><span>Waga:</span> <strong>{Number(product.weight).toFixed(2)} kg</strong></div>}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {relatedProducts.length > 0 && (
                        <div className="pp-related-products">
                            <h2>Powiązane produkty</h2>
                            <div className="pp-related-grid">
                                {relatedProducts.map((item) => (
                                    <ProductCard key={item._id} product={item} />
                                ))}
                            </div>
                        </div>
                    )}

                    {similarProducts.length > 0 && (
                        <div className="pp-similar-products">
                            <h2>Podobne produkty</h2>
                            <div className="pp-similar-grid">
                                {similarProducts.map((item) => (
                                    <ProductCard key={item._id} product={item} />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {toast && <Toast message={toast} onClose={() => setToast('')} />}
        </div>
    );
}

export default PageProductDetails;